import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Undo,
  Redo,
  Sparkles,
  ChevronDown,
  Search,
} from 'lucide-react';

export interface TemplateVariableItem {
  key: string;
  label?: string;
  category?: string;
}

interface TiptapRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  templateVariables?: Array<string | TemplateVariableItem>;
  minHeight?: string;
}

export const TiptapRichTextEditor: React.FC<TiptapRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Compose notification content...',
  templateVariables = [],
  minHeight = '140px',
}) => {
  const [showVarDropdown, setShowVarDropdown] = useState(false);
  const [varFilter, setVarFilter] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [openUpward, setOpenUpward] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-orange-400 underline hover:text-orange-300',
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class:
          'tiptap-content focus:outline-none px-3 py-2.5 text-gray-900 dark:text-gray-100 text-xs leading-relaxed font-sans',
        style: `min-height: ${minHeight};`,
      },
    },
  });

  // Sync value from outside if changed externally and editor is not focused
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Check if value is truly different to avoid cursor jumps
      if (!editor.isFocused) {
        editor.commands.setContent(value || '', { emitUpdate: false });
      }
    }
  }, [value, editor]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldOpenUp = spaceBelow < dropdownHeight + 10 && rect.top > dropdownHeight;

      setOpenUpward(shouldOpenUp);
      setDropdownPos({
        top: shouldOpenUp ? rect.top - 6 : rect.bottom + 6,
        right: Math.max(10, window.innerWidth - rect.right),
      });
    }
  };

  useEffect(() => {
    if (showVarDropdown) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showVarDropdown]);

  // Close variable dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        portalRef.current && !portalRef.current.contains(target)
      ) {
        setShowVarDropdown(false);
      }
    };
    if (showVarDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVarDropdown]);

  const insertVariable = (variable: string) => {
    if (!editor) return;
    const formatted = `{{ ${variable.trim()} }}`;
    editor.chain().focus().insertContent(formatted).run();
    setShowVarDropdown(false);
    setVarFilter('');
  };

  const normalizedVars = useMemo<TemplateVariableItem[]>(() => {
    return (templateVariables || []).map((v) => {
      if (typeof v === 'string') {
        return { key: v, category: 'General' };
      }
      return {
        key: v.key,
        label: v.label,
        category: v.category || 'General',
      };
    });
  }, [templateVariables]);

  const filteredVars = useMemo<TemplateVariableItem[]>(() => {
    if (!varFilter.trim()) return normalizedVars;
    const q = varFilter.toLowerCase();
    return normalizedVars.filter(
      (v: TemplateVariableItem) =>
        v.key.toLowerCase().includes(q) ||
        (v.label && v.label.toLowerCase().includes(q)) ||
        (v.category && v.category.toLowerCase().includes(q))
    );
  }, [normalizedVars, varFilter]);

  const groupedVars = useMemo<Record<string, TemplateVariableItem[]>>(() => {
    const groups: Record<string, TemplateVariableItem[]> = {};
    filteredVars.forEach((v: TemplateVariableItem) => {
      const cat = v.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(v);
    });
    return groups;
  }, [filteredVars]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden bg-white dark:bg-gray-950 focus-within:border-orange-500/50 transition-colors shadow-inner flex flex-col">
      {/* Top Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-1 p-1.5 border-b border-gray-200 dark:border-gray-800/80 bg-gray-50/80 dark:bg-gray-900/60 backdrop-blur-sm select-none">
        {/* Formatting Buttons Group */}
        <div className="flex items-center gap-0.5 flex-wrap">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              editor.isActive('bold')
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 font-bold'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Bold (Ctrl+B)"
          >
            <Bold className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              editor.isActive('italic')
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 font-bold'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Italic (Ctrl+I)"
          >
            <Italic className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              editor.isActive('underline')
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 font-bold'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              editor.isActive('strike')
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 font-bold'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Strikethrough"
          >
            <Strikethrough className="w-3.5 h-3.5" />
          </button>

          <div className="w-px h-4 bg-gray-200 dark:bg-gray-800 mx-1" />

          {/* Headings */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`px-1.5 py-1 rounded-lg text-[11px] font-bold transition-colors cursor-pointer ${
              editor.isActive('heading', { level: 1 })
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Heading 1"
          >
            <Heading1 className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`px-1.5 py-1 rounded-lg text-[11px] font-bold transition-colors cursor-pointer ${
              editor.isActive('heading', { level: 2 })
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Heading 2"
          >
            <Heading2 className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`px-1.5 py-1 rounded-lg text-[11px] font-bold transition-colors cursor-pointer ${
              editor.isActive('heading', { level: 3 })
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Heading 3"
          >
            <Heading3 className="w-3.5 h-3.5" />
          </button>

          <div className="w-px h-4 bg-gray-200 dark:bg-gray-800 mx-1" />

          {/* Lists */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              editor.isActive('bulletList')
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Bullet List"
          >
            <List className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
              editor.isActive('orderedList')
                ? 'bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title="Numbered List"
          >
            <ListOrdered className="w-3.5 h-3.5" />
          </button>

          <div className="w-px h-4 bg-gray-200 dark:bg-gray-800 mx-1" />

          {/* Undo / Redo */}
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="p-1.5 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
            title="Undo"
          >
            <Undo className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="p-1.5 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
            title="Redo"
          >
            <Redo className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Insert Variables Button & Dropdown */}
        <div>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => {
              if (!showVarDropdown) {
                updatePosition();
              }
              setShowVarDropdown(!showVarDropdown);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-orange-50 dark:bg-orange-500/10 hover:bg-orange-100 dark:hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 transition-all cursor-pointer shadow-sm"
          >
            <Sparkles className="w-3 h-3 text-orange-500 dark:text-orange-400" />
            <span>Insert Variable</span>
            <ChevronDown className={`w-3 h-3 text-orange-500 dark:text-orange-400 transition-transform duration-200 ${showVarDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showVarDropdown && typeof document !== 'undefined' && createPortal(
            <div
              ref={portalRef}
              className="fixed z-[10001] w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-2.5 text-xs flex flex-col space-y-2 animate-in fade-in zoom-in-95 duration-100"
              style={{
                top: dropdownPos.top,
                right: dropdownPos.right,
                transform: openUpward ? 'translateY(-100%)' : 'none',
              }}
            >
              <div className="flex items-center justify-between pb-1 border-b border-gray-100 dark:border-gray-800">
                <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">
                  Template Variables
                </span>
                <span className="text-[9px] text-orange-600 dark:text-orange-400 font-mono">
                  {filteredVars.length} available
                </span>
              </div>

              {/* Search filter for variables */}
              <div className="relative">
                <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter variables..."
                  value={varFilter}
                  onChange={(e) => setVarFilter(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md pl-6 pr-2 py-1 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/60"
                  autoFocus
                />
              </div>

              <div
                className="max-h-56 overflow-y-auto custom-scroll space-y-2 pt-1"
                onWheel={(e) => e.stopPropagation()}
              >
                {filteredVars.length === 0 ? (
                  <div className="py-3 text-center text-[11px] text-gray-500">
                    No matching variables
                  </div>
                ) : (
                  (Object.entries(groupedVars) as [string, TemplateVariableItem[]][]).map(([catName, items]) => (
                    <div key={catName} className="space-y-0.5">
                      <div className="px-2 py-0.5 text-[9px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider bg-orange-50 dark:bg-orange-500/10 rounded flex items-center justify-between mb-1">
                        <span className="truncate">{catName}</span>
                        <span className="text-[8px] font-mono text-orange-600/60 dark:text-orange-400/60 font-normal">
                          {items.length}
                        </span>
                      </div>
                      {items.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => insertVariable(item.key)}
                          className="w-full text-left px-2 py-1 rounded-md text-[11px] font-mono text-cyan-700 dark:text-cyan-300 hover:text-gray-900 dark:hover:text-white hover:bg-orange-50 dark:hover:bg-orange-500/20 transition-colors flex items-center justify-between group cursor-pointer"
                          title={`Click to insert {{ ${item.key} }}`}
                        >
                          <div className="truncate pr-2">
                            <span>{`{{ ${item.key} }}`}</span>
                            {item.label && item.label !== item.key && (
                              <div className="text-[9px] text-gray-500 dark:text-gray-400 font-sans truncate">
                                {item.label}
                              </div>
                            )}
                          </div>
                          <span className="text-[9px] text-gray-400 group-hover:text-orange-600 dark:group-hover:text-orange-400 font-sans ml-2 shrink-0">
                            Insert
                          </span>
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Editor Content Area */}
      <div className="flex-1 cursor-text bg-white dark:bg-gray-950">
        <EditorContent editor={editor} />
      </div>

      {/* Inline styles for editor elements */}
      <style>{`
        .is-editor-empty:before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
          font-style: italic;
        }
        .tiptap-content h1, .ProseMirror h1 {
          display: block !important;
          font-size: 1.5rem !important;
          font-weight: 700 !important;
          color: inherit !important;
          margin: 12px 0 6px 0 !important;
          line-height: 1.25 !important;
        }
        .tiptap-content h2, .ProseMirror h2 {
          display: block !important;
          font-size: 1.25rem !important;
          font-weight: 700 !important;
          color: inherit !important;
          margin: 10px 0 4px 0 !important;
          line-height: 1.3 !important;
        }
        .tiptap-content h3, .ProseMirror h3 {
          display: block !important;
          font-size: 1.1rem !important;
          font-weight: 600 !important;
          color: inherit !important;
          margin: 8px 0 4px 0 !important;
          line-height: 1.35 !important;
        }
        .tiptap-content ul, .ProseMirror ul {
          display: block !important;
          list-style-type: disc !important;
          padding-left: 24px !important;
          margin: 8px 0 !important;
        }
        .tiptap-content ol, .ProseMirror ol {
          display: block !important;
          list-style-type: decimal !important;
          padding-left: 24px !important;
          margin: 8px 0 !important;
        }
        .tiptap-content li, .ProseMirror li {
          display: list-item !important;
          margin-bottom: 2px !important;
        }
        .tiptap-content p, .ProseMirror p {
          margin: 0 0 6px 0 !important;
        }
        .tiptap-content p:last-child, .ProseMirror p:last-child {
          margin-bottom: 0 !important;
        }
        .tiptap-content strong, .ProseMirror strong {
          font-weight: 700 !important;
          color: inherit !important;
        }
      `}</style>
    </div>
  );
};
export default TiptapRichTextEditor;
